/**
 * Maps technical ZUGFeRD / Factur-X errors to short German user-facing hints.
 * Used by the zugferd-pdf API and (via the same module path) the offer PDF client as fallback.
 */

/** Actionable hints when buyer address fields are obviously incomplete (before/after server errors). */
export function getLikelyEInvoiceRecipientHint(opts: {
  nameLine: string | null | undefined;
  street: string | null | undefined;
  plzOrt: string | null | undefined;
}): string | null {
  const nameOk = (opts.nameLine ?? "").trim();
  if (!nameOk) {
    return "Der Empfängername fehlt — bitte im Empfängerblock mindestens eine Namenszeile ausfüllen.";
  }
  const street = (opts.street ?? "").trim();
  if (!street) {
    return "Straße und Hausnummer fehlen — bitte im Empfängerblock ergänzen.";
  }
  const plzOrt = (opts.plzOrt ?? "").trim();
  if (!plzOrt) {
    return "Postleitzahl und Ort fehlen — bitte in einer Zeile im Format „12345 Ort“ eintragen.";
  }
  // COMFORT profile expects parseable DE postcode + city (see buildFacturXData.parseRecipientPostcode).
  if (!/^(\d{4,6})\s+\S/.test(plzOrt)) {
    return "Postleitzahl und Ort sollten mit einer gültigen PLZ beginnen (z. B. „10115 Berlin“), damit die E-Rechnung normkonform erzeugt werden kann.";
  }
  return null;
}

/**
 * Identifiers the official EN 16931 schematron demands for 0 %-VAT modes (factur-x-kit gets BR-AE-2 wrong
 * on the buyer side: it accepts the Steuernummer and rejects the Handelsregisternummer).
 * - Reverse charge (BR-AE-02): seller USt-IdNr. or Steuernummer (BT-31/BT-32) and
 *   buyer USt-IdNr. or Handelsregisternr. (BT-48/BT-47).
 * - Innergemeinschaftliche Lieferung (BR-IC-02): seller and buyer USt-IdNr.
 */
export function getMissingEInvoiceTaxIdentifiers(opts: {
  vatMode: string | null | undefined;
  recipientVatId?: string | null;
  recipientLegalRegistrationId?: string | null;
  sellerVatId?: string | null;
  sellerTaxNumber?: string | null;
}): { buyer: boolean; seller: boolean; buyerAcceptsLegalRegistration: boolean } {
  const mode = (opts.vatMode ?? "").trim();
  const has = (v: string | null | undefined) => Boolean((v ?? "").trim());
  if (mode === "vat_0_reverse_charge") {
    return {
      buyer: !has(opts.recipientVatId) && !has(opts.recipientLegalRegistrationId),
      seller: !has(opts.sellerVatId) && !has(opts.sellerTaxNumber),
      buyerAcceptsLegalRegistration: true,
    };
  }
  if (mode === "vat_0_innergemeinschaftliche_lieferung") {
    return {
      buyer: !has(opts.recipientVatId),
      seller: !has(opts.sellerVatId),
      buyerAcceptsLegalRegistration: false,
    };
  }
  return { buyer: false, seller: false, buyerAcceptsLegalRegistration: false };
}

/** Hint when reverse-charge / intra-community e-invoices are missing buyer tax identifiers. */
export function getLikelyEInvoiceVatHint(opts: {
  vatMode: string | null | undefined;
  recipientVatId?: string | null;
  recipientTaxNumber?: string | null;
  recipientLegalRegistrationId?: string | null;
}): string | null {
  const mode = (opts.vatMode ?? "").trim();
  if (!getMissingEInvoiceTaxIdentifiers(opts).buyer) return null;
  if (mode === "vat_0_innergemeinschaftliche_lieferung") {
    return "Für innergemeinschaftliche Lieferungen braucht die E-Rechnung die USt-IdNr. des Empfängers. Bitte unter Kontakte → Organisation ergänzen.";
  }
  return "Für eine normkonforme Reverse-Charge-E-Rechnung (§ 13b) braucht der Empfänger eine USt-IdNr. oder Handelsregisternummer; die Steuernummer reicht nicht (EN 16931, Regel BR-AE-2). Bitte unter Kontakte → Organisation eintragen.";
}

/** Hint when the sender lacks the identifier BR-AE-2 / BR-IC-2 require. */
export function getLikelyEInvoiceVatSellerHint(opts: {
  vatMode: string | null | undefined;
  vatId?: string | null;
  taxNumber?: string | null;
}): string | null {
  const mode = (opts.vatMode ?? "").trim();
  const missing = getMissingEInvoiceTaxIdentifiers({
    vatMode: mode,
    sellerVatId: opts.vatId,
    sellerTaxNumber: opts.taxNumber,
  });
  if (!missing.seller) return null;
  if (mode === "vat_0_innergemeinschaftliche_lieferung") {
    return "Für innergemeinschaftliche Lieferungen braucht die E-Rechnung Ihre USt-IdNr. Bitte unter Einstellungen → Firma ergänzen.";
  }
  return "Für eine Reverse-Charge-E-Rechnung braucht Ihre Firma eine USt-IdNr. oder Steuernummer (EN 16931, Regel BR-AE-2). Bitte unter Einstellungen → Firma ergänzen.";
}

/** Hint when the sender has none of the identifiers accepted by BR-CO-26. */
export function getLikelyEInvoiceSellerHint(opts: {
  vatId?: string | null;
  taxNumber?: string | null;
  commercialRegisterNumber?: string | null;
}): string | null {
  if ((opts.vatId ?? "").trim()) return null;
  if ((opts.taxNumber ?? "").trim()) return null;
  if ((opts.commercialRegisterNumber ?? "").trim()) return null;
  return SELLER_IDENTIFIER_MESSAGE;
}

/**
 * factur-x-kit schreibt `ram:ChargeAmount` (BT-146) nur bei einem truthy Preis. Eine Position mit
 * 0,00 € erzeugt daher XML ohne dieses Pflichtfeld und die Serialisierung bricht mit
 * „Received Invalid Data Value“ ab. Solche Zeilen tragen 0 € zur Summe bei — sie fallen aus dem
 * XML, alle Beträge bleiben unverändert.
 */
export function isZugferdSerializableLineItem(item: { unitPriceNet: number }): boolean {
  return Number.isFinite(item.unitPriceNet) && item.unitPriceNet !== 0;
}

/**
 * Hint when positions without a price are part of the document. They stay in the PDF, but the
 * embedded e-invoice omits them.
 */
export function getZeroPricePositionsHint(count: number): string | null {
  if (count <= 0) return null;
  const subject =
    count === 1 ? "Eine Position hat keinen Preis" : `${count} Positionen haben keinen Preis`;
  return `${subject} (0,00 €). Diese Zeilen stehen im PDF, fehlen aber in der eingebetteten E-Rechnung (ZUGFeRD/XML) — die Summen bleiben unverändert. Bitte prüfen, ob der Preis fehlt oder die Zeile eine reine Beschreibung ist.`;
}

const SELLER_IDENTIFIER_MESSAGE =
  "Für die E-Rechnung braucht der Absender eine eindeutige Kennung: USt-IdNr., Steuernummer oder Handelsregisternummer (EN 16931, Regel BR-CO-26). Bitte unter Einstellungen → Firma prüfen. Das PDF ist lesbar, ohne ZUGFeRD/XML.";

export function joinZugferdWarningMessages(parts: Array<string | null | undefined>): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of parts) {
    for (const block of String(raw ?? "").split(/\n\n+/)) {
      const t = block.trim();
      if (!t || seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
  }
  return out.join("\n\n");
}

export function extractZugferdBrCodes(technical: string): string[] {
  const matches = String(technical ?? "").match(/BR-[A-Z]+-\d+/gi) ?? [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of matches) {
    const code = m.toUpperCase();
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out;
}

export function formatZugferdValidationDetails(
  errors: Array<{ message?: string; path?: Array<string | number> }>,
): string {
  return errors
    .map((e) => String(e.message ?? "").trim())
    .filter(Boolean)
    .join(" | ");
}

const ZUGFERD_BR_MESSAGES: Array<{ test: (raw: string, t: string) => boolean; message: string }> = [
  {
    test: (raw, t) => /br-s-2\b/i.test(raw) || /invoiced item vat category code.*standard rate/i.test(t),
    message:
      "Bei normalem Mehrwertsteuersatz muss im Unternehmensprofil die USt-IdNr. und/oder eine Steuernummer hinterlegt sein (EN 16931, Regel BR-S-2). Bitte unter Einstellungen → Firma ergänzen. Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
  {
    test: (raw, t) =>
      /br-co-26\b/i.test(raw) ||
      /seller vat identifier \(bt-31\)|seller identifier.*bt-29|seller legal registration.*bt-30/i.test(t),
    message: SELLER_IDENTIFIER_MESSAGE,
  },
  {
    test: (raw) => /br-16\b/i.test(raw),
    message:
      "Das Dokument enthält keine Position mit Preis. Eine E-Rechnung braucht mindestens eine bepreiste Position (EN 16931, Regel BR-16). Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
  {
    test: (raw) => /br-ae-2\b|br-ae-3\b|br-ae-4\b/i.test(raw),
    message:
      "Bei Reverse-Charge (§ 13b) braucht die E-Rechnung die USt-IdNr. oder Steuernummer Ihrer Firma und die USt-IdNr. oder Handelsregisternummer des Kunden; dessen Steuernummer reicht nicht (EN 16931, Regel BR-AE-2). Bitte Einstellungen → Firma und die Organisation des Kunden prüfen. Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
  {
    test: (raw) => /br-ae-10\b/i.test(raw),
    message:
      "Bei Reverse-Charge fehlt der Steuerbefreiungsgrund in der E-Rechnung (EN 16931, Regel BR-AE-10). Bitte Support informieren. Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
  {
    test: (raw) => /br-e-10\b/i.test(raw),
    message:
      "Bei steuerfreier Rechnung fehlt der gesetzliche Befreiungsgrund in der E-Rechnung (EN 16931, Regel BR-E-10). Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
  {
    test: (raw) => /br-z-10\b/i.test(raw),
    message:
      "Bei Nullsteuersatz (Kategorie Z) darf die E-Rechnung keinen Steuerbefreiungsgrund enthalten (EN 16931, Regel BR-Z-10). Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
  {
    test: (raw) => /br-s-5\b/i.test(raw),
    message:
      "Bei normalem Mehrwertsteuersatz (Kategorie S) muss der Steuersatz größer als 0 % sein (EN 16931, Regel BR-S-5). Bitte MwSt.-Modus prüfen. Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
  {
    test: (raw) => /br-ic-2\b|br-ic-3\b|br-ic-4\b/i.test(raw),
    message:
      "Bei innergemeinschaftlicher Lieferung müssen Absender- und Empfänger-USt-IdNr. in der E-Rechnung stehen (EN 16931, Regel BR-IC-2). Bitte unter Kontakte → Organisation die USt-IdNr. ergänzen. Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
  {
    test: (raw) => /br-ic-10\b/i.test(raw),
    message:
      "Bei innergemeinschaftlicher Lieferung fehlt der Steuerbefreiungsgrund in der E-Rechnung (EN 16931, Regel BR-IC-10). Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
  {
    test: (raw) => /br-ic-11\b/i.test(raw),
    message:
      "Bei innergemeinschaftlicher Lieferung muss ein Lieferdatum oder Leistungszeitraum gesetzt sein (EN 16931, Regel BR-IC-11). Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
  {
    test: (raw) => /br-ic-12\b/i.test(raw),
    message:
      "Bei innergemeinschaftlicher Lieferung muss das Lieferland in der E-Rechnung stehen (EN 16931, Regel BR-IC-12). Bitte Empfängeradresse prüfen. Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.",
  },
];

export function collectZugferdUserMessages(technical: string): string[] {
  const raw = (technical ?? "").trim();
  const t = raw.toLowerCase();
  const matched = ZUGFERD_BR_MESSAGES.filter((rule) => rule.test(raw, t)).map((rule) => rule.message);
  if (matched.length > 0) return matched;

  const fallbacks: string[] = [];

  if (/unknown or not implemented profile/i.test(t)) {
    fallbacks.push(
      "Dieser Belegtyp oder das gewählte Profil wird für die eingebettete E-Rechnung (ZUGFeRD/Factur-X) nicht unterstützt. Das PDF ist lesbar, ohne normkonformes XML — ggf. anderen Dokumenttyp prüfen oder Support informieren.",
    );
  }
  if (/profile validation failed|there are errors in the given data/i.test(t)) {
    fallbacks.push(
      "Die Rechnungsdaten erfüllen die Prüfregeln für eine E-Rechnung nicht (Profil-Validierung). Häufig fehlen vollständige Absender-Steuerdaten oder Angaben passen nicht zur MwSt.-Logik. Bitte Unternehmenseinstellungen und Empfängeradresse prüfen. Das PDF ist lesbar, ohne ZUGFeRD/XML.",
    );
  }
  if (fallbacks.length > 0) return fallbacks;

  return [];
}

export function userMessageForZugferdPdfError(technical: string): string {
  const raw = (technical ?? "").trim();
  const t = raw.toLowerCase();
  const stripOurPrefix = raw.replace(/^ZUGFeRD-Verarbeitung fehlgeschlagen:\s*/i, "").trim();
  const inner = stripOurPrefix.toLowerCase();

  const fromRules = collectZugferdUserMessages(raw);
  if (fromRules.length > 0) {
    const codes = extractZugferdBrCodes(raw);
    return joinZugferdWarningMessages([
      ...fromRules,
      codes.length > 0 ? `Regeln: ${codes.join(", ")}` : null,
    ]);
  }

  // Request / payload (server-side)
  if (/pdf-datei fehlt|field:\s*pdf|multipart/i.test(t)) {
    return "Die PDF-Datei wurde nicht vollständig übertragen. Bitte die Vorschau schließen und erneut öffnen.";
  }
  if (/invoicedata json fehlt|invoicedata ist kein gültiges json/i.test(t)) {
    return "Die Rechnungsstammdaten für die E-Rechnung konnten nicht gelesen werden. Bitte Seite neu laden und die Vorschau erneut öffnen.";
  }

  // Buyer / recipient (common in validators)
  if (
    /\bbuyer\b|\bship-?to\b|\bempfänger\b|\brecipient\b/i.test(raw) ||
    /\bpostaladdress\b|\baddresslineone\b|\baddress line/i.test(t) ||
    (/\bpostcode\b|\bplz\b|\bzip\b/.test(inner) && /\bbuyer\b|\bship/i.test(inner)) ||
    (/\bcity\b/.test(inner) && /\bbuyer\b/i.test(inner)) ||
    /\bstreet\b.*\bbuyer\b|\bbuyer\b.*\bstreet\b/i.test(t)
  ) {
    return "Die Empfängeradresse ist für die E-Rechnung unzureichend (Name, Straße, PLZ und Ort müssen nachvollziehbar sein). Das PDF ist lesbar, enthält aber kein gültiges ZUGFeRD/XML.";
  }

  // Seller / own company data (incl. BT-* hints from validators)
  if (
    /\bseller\b|\blieferant\b|\bcompany_name\b|\bfirmenname\b/i.test(raw) ||
    (/\bvat\b|\bust.?id\b|\bsteuernummer\b|\btax\b/.test(inner) && /\bseller\b/i.test(inner)) ||
    /\bpostaladdress\b.*\bseller\b|\bseller\b.*\bpostaladdress\b/i.test(t) ||
    /\(bt-31\)|\(bt-32\)|\(bt-29\)|\(bt-30\)|seller tax registration/i.test(t)
  ) {
    return "Die Absender-/Firmendaten (Adresse, ggf. USt-IdNr.) reichen für die E-Rechnung nicht aus oder sind unvollständig. Bitte in den Unternehmenseinstellungen prüfen. Das PDF ist lesbar, ohne normkonformes ZUGFeRD/XML.";
  }

  // Line items / catalogue
  if (
    /\bline\b|\bposition\b|\binvoiceline\b|\bproductdescription\b|\bquantity\b|\bunit\b/i.test(
      raw
    ) && /invalid|missing|required|must|error/i.test(t)
  ) {
    return "Mindestens eine Position oder Mengeneinheit entspricht nicht den Anforderungen der E-Rechnung. Bitte Positionen und Einheiten prüfen. Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.";
  }

  // Totals / tax mismatch
  if (
    /\btotal\b|\bbrutto\b|\bnetto\b|\bgross\b|\bamount\b|\brounding\b|\bmismatch\b|\bdifferenz\b/i.test(
      t
    ) &&
    /\btax\b|\bvat\b|\bust\b|\bsteuer\b|\bbetrag\b/i.test(t)
  ) {
    return "Die Beträge oder Steueraufschlüsselung passen nicht zu den Anforderungen der E-Rechnung (z. B. Summenabweichung). Bitte Rabatt, MwSt.-Modus und Positionen prüfen. Das PDF ist lesbar, ohne gültiges ZUGFeRD/XML.";
  }

  // PDF/A or embedding
  if (
    /\bpdf\b.*\b(a-?3|\/a)\b|\bpdfa\b|\bembedd|\battachment\b|\bfactur-?x\b|\bzugferd\b|\bxml\b|\bschema\b|\bconform/i.test(
      t
    )
  ) {
    return "Das PDF konnte nicht als konforme E-Rechnung (PDF/A-3 mit eingebetteter XML) aufbereitet werden. Das normale PDF ist dennoch vollständig lesbar, erfüllt aber nicht die E-Rechnungspflicht.";
  }

  // Short technical tail for support (still German framing)
  const shortTech = stripOurPrefix.length > 180 ? `${stripOurPrefix.slice(0, 177)}…` : stripOurPrefix;
  if (shortTech) {
    return `Die E-Rechnungs-Daten konnten nicht eingebettet werden. Das PDF ist vollständig lesbar, ohne ZUGFeRD/XML. Technischer Hinweis: ${shortTech}`;
  }

  return "Die E-Rechnungs-Daten (ZUGFeRD/Factur-X) konnten nicht eingebettet werden. Das PDF ist vollständig lesbar, erfüllt aber nicht die E-Rechnungspflicht.";
}
